/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { currencyFormatter } from "@/app/lib/currency-formatter";
import { formatDate } from "@/app/lib/format-date";
import { volumeFormatter } from "@/app/lib/volume-formatter";
import { TradeReportQuery } from "@/app/types/generated/graphql";

import { compileHeadersAndDataForCSV, createEmptyRow, mapDataValuesToHeaderKeys } from "../csv-helpers";

export type TradingSummaryHeaders =
  | "membershipId"
  | "memberName"
  | "email"
  | "totalTraded"
  | "totalSold"
  | "gstReceived"
  | "receivedAmount"
  | "sellerFees"
  | "sellerFeesGST"
  | "totalBought"
  | "gstPaid"
  | "paidAmount"
  | "buyerFees"
  | "buyerFeesGST";

const getTradingSummaryData = (tradingSummary: TradeReportQuery["tradeReport"]): Record<string, string> => {
  return {
    "Start date": formatDate(tradingSummary.startDate, { noTZ: true }),
    "End date": formatDate(tradingSummary.endDate, { noTZ: true }),
    "Total Traded volume": volumeFormatter(tradingSummary.totalTradedVolume),
  };
};

const getTradeDetailHeaders = (): Record<TradingSummaryHeaders, string> => {
  return {
    membershipId: "Membership ID",
    memberName: "Member name",
    email: "Authorised User Email",
    totalTraded: "Carbon Credits Traded",
    totalSold: "Sold",
    gstReceived: "GST Received",
    receivedAmount: "Received Amount",
    sellerFees: "Seller Fees",
    sellerFeesGST: "Seller Fees GST",
    totalBought: "Bought",
    gstPaid: "GST Paid",
    paidAmount: "Paid Amount",
    buyerFees: "Buyer Fees",
    buyerFeesGST: "Buyer Fees GST",
  };
};

const getTradeReportDetailForAccount = (
  tradeReport: TradeReportQuery["tradeReport"]["tradeReportDetails"]["0"],
  currencySymbol = "$",
): Record<TradingSummaryHeaders, string> => {
  return {
    membershipId: tradeReport.account.members?.[0]?.membershipId ?? "-",
    memberName: tradeReport.account.members?.[0]?.name ?? "-",
    email: tradeReport.account.email,
    totalTraded: volumeFormatter(+calculateTotalTradedVolume(tradeReport)),
    totalSold: volumeFormatter(tradeReport.tradedAskVolume),
    gstReceived: currencyFormatter(currencySymbol, tradeReport.totalTradeGSTReceived.amount),
    receivedAmount: currencyFormatter(currencySymbol, tradeReport.receivedAmount.amount),
    sellerFees: currencyFormatter(currencySymbol, tradeReport.totalSellerFees.amount),
    sellerFeesGST: currencyFormatter(currencySymbol, tradeReport.totalSellerFeesGST.amount),
    totalBought: volumeFormatter(tradeReport.tradedBidVolume),
    gstPaid: currencyFormatter(currencySymbol, tradeReport.totalTradeGSTPaid.amount),
    paidAmount: currencyFormatter(currencySymbol, tradeReport.paidAmount.amount),
    buyerFees: currencyFormatter(currencySymbol, tradeReport.totalBuyerFees.amount),
    buyerFeesGST: currencyFormatter(currencySymbol, tradeReport.totalBuyerFeesGST.amount),
  };
};

/**
 * Helper to ensure the sum is correctly calculated if the numbers come in as strings or numbers
 * @param accountSummary
 */
export const calculateTotalTradedVolume = (
  tradeReportDetail: TradeReportQuery["tradeReport"]["tradeReportDetails"]["0"],
) => {
  return Number(tradeReportDetail.tradedAskVolume) + Number(tradeReportDetail.tradedBidVolume);
};

const getTradeReportForAccount = (
  tradingSummaryDetails: TradeReportQuery["tradeReport"]["tradeReportDetails"],
  currencySymbol = "$",
): Record<TradingSummaryHeaders, string>[] => {
  const data: Record<TradingSummaryHeaders, string>[] = [];
  if (tradingSummaryDetails.length) {
    data.push(getTradeDetailHeaders());
  }

  for (const tradingSummaryDetail of tradingSummaryDetails) {
    const accountData = getTradeReportDetailForAccount(tradingSummaryDetail, currencySymbol);
    data.push(accountData);
  }

  return data;
};

export const createTradingSummaryDataForCSV = (
  tradingSummary: TradeReportQuery["tradeReport"],
  currencySymbol = "$",
) => {
  const tradingSummaryData = getTradingSummaryData(tradingSummary);
  const tradeSummariesForAccount = getTradeReportForAccount(tradingSummary.tradeReportDetails, currencySymbol);

  const modifiedAccountSummaryData = tradeSummariesForAccount.map((accountSummary) =>
    mapDataValuesToHeaderKeys(tradingSummaryData, accountSummary),
  );

  return compileHeadersAndDataForCSV(
    [tradingSummaryData, createEmptyRow(tradingSummaryData)],
    modifiedAccountSummaryData,
  );
};
